<template>
  <div class="kit-material-workbench assembly-workbench-view" ref="workbench">
    <div class="assembly-workbench">
      <!-- 左侧半成品 SKU 侧边栏 -->
      <aside class="assembly-sidebar" :style="{ width: assemblySidebarWidth + 'px' }">
        <div class="assembly-sidebar-table">
          <el-table
            ref="assemblySidebarTable"
            v-loading="assemblyLiveLoading"
            class="shadcn-data-table"
            data-ui="data-table"
            :data="assemblyRows"
            height="100%"
            row-key="sku"
            highlight-current-row
            empty-text="暂无半成品"
            :row-class-name="assemblyRowClassName"
            @dragstart.native="startAssemblyRowDrag"
            @dragover.native.prevent="overAssemblyRowDrag"
            @drop.native="dropAssemblyRowDrag"
            @dragend.native="endAssemblyRowDrag"
            @row-click="selectAssemblyRow">
            <el-table-column label="" width="34" align="center">
              <template slot-scope="scope">
                <el-button class="assembly-remove-button shadcn-button shadcn-button--ghost" type="text" size="mini" icon="el-icon-delete" title="移除" @click.stop="removeAssemblyRow(scope.row)"></el-button>
              </template>
            </el-table-column>
            <el-table-column prop="parentProduct" label="母件编码" width="112" show-overflow-tooltip>
              <template slot-scope="scope">
                <div class="assembly-sku-cell">
                  <span class="assembly-order-index">{{ scope.$index + 1 }}</span>
                  <el-tooltip placement="right" effect="light" :open-delay="180" popper-class="assembly-sku-bom-tooltip-popper">
                    <div slot="content" class="assembly-sku-bom-tooltip">
                      <div class="assembly-sku-bom-tooltip__title">{{ scope.row.sku }} BOM</div>
                      <div v-if="!assemblySkuOptionForRow(scope.row) || assemblySkuOptionForRow(scope.row).bomLoading" class="assembly-sku-bom-tooltip__empty">正在读取 BOM...</div>
                      <div v-else-if="assemblySkuOptionForRow(scope.row).bomError" class="assembly-sku-bom-tooltip__empty">BOM 读取失败</div>
                      <div v-else-if="!assemblySkuOptionForRow(scope.row).bom.length" class="assembly-sku-bom-tooltip__empty">暂无 BOM 物料</div>
                      <div v-else class="assembly-sku-bom-tooltip__viewport">
                        <div class="assembly-sku-bom-tooltip__table">
                          <div class="assembly-sku-bom-tooltip__row assembly-sku-bom-tooltip__row--head">
                            <span>序号</span><span>物料编码</span><span>物料名称</span><span>用量</span>
                          </div>
                          <div v-for="(part, partIndex) in assemblySkuOptionForRow(scope.row).bom" :key="part.rowKey" class="assembly-sku-bom-tooltip__row">
                            <span>{{ partIndex + 1 }}</span><span>{{ part.matNo }}</span><span>{{ part.matName }}</span><span>{{ part.qtyPer }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span class="assembly-sku-text" @mouseenter="loadAssemblyBom(assemblySkuOptionForRow(scope.row))">{{ scope.row.parentProduct || scope.row.sku }}</span>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="documentNo" label="单据编码" width="92" show-overflow-tooltip></el-table-column>
            <el-table-column prop="name" label="产品名称" width="130" show-overflow-tooltip></el-table-column>
            <el-table-column prop="exclusiveBuildableQty" label="独占可制作数" width="100" align="center" :formatter="assemblyRowExclusiveBuildableQty"></el-table-column>
            <el-table-column label="预制作数" width="84" align="center">
              <template slot-scope="scope">
                <el-input
                  v-model="scope.row.planQty"
                  class="assembly-plan-qty-input"
                  size="mini"
                  @input="handleAssemblyPlanQtyInput(scope.row, $event)"
                  @focus="selectAssemblyRow(scope.row)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="结果" width="88" align="center">
              <template slot-scope="scope">
                <el-tag data-ui="badge" size="mini" effect="plain" :type="assemblyResultTagType(assemblyRowResults[scope.row.sku])">
                  {{ assemblyRowResults[scope.row.sku] ? assemblyRowResults[scope.row.sku].label : '未排产' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="assembly-sidebar-footer">
          <el-button class="shadcn-button shadcn-button--primary" type="primary" size="small" icon="el-icon-plus" @click="openAssemblyCatalog">添加半成品</el-button>
        </div>
      </aside>

      <!-- 左右分屏拖拽条 -->
      <div
        class="assembly-column-splitter"
        role="separator"
        title="拖动调整左右区域宽度"
        @mousedown="startAssemblyColumnResize"></div>

      <!-- 右侧三层堆叠工作区 -->
      <div class="assembly-detail-stack" ref="assemblyDetailStack" :style="assemblyDetailStyle">
        <!-- 第 1 层：选中半成品库存物料 -->
        <section class="assembly-layer" aria-label="选中半成品库存物料">
          <div class="assembly-layer-head">
            <div class="assembly-layer-title">
              <span>选中半成品库存物料</span>
              <span v-if="activeAssemblyItem" class="assembly-layer-meta">{{ activeAssemblyItem.sku }} · {{ activeAssemblyItem.name }}</span>
              <el-button v-if="activeAssemblyItem && activeAssemblyItem.bomError" class="shadcn-button shadcn-button--ghost" type="text" size="mini" icon="el-icon-refresh" @click="loadAssemblyBom(activeAssemblyItem)">重试 BOM</el-button>
            </div>
            <span class="assembly-layer-meta">物料 {{ activeAssemblyBomRows.length }} 项 · 独占可制作 {{ activeAssemblyItem ? assemblyExclusiveBuildableQty(activeAssemblyItem) : 0 }}</span>
          </div>
          <div v-if="assemblyBomFieldError" role="alert" class="assembly-bom-field-error">
            <span>{{ assemblyBomFieldError }}</span>
            <el-button type="text" size="mini" :loading="assemblyBomFieldLoading" @click="loadAssemblyBomFields">重试字段加载</el-button>
          </div>
          <div class="assembly-layer-table">
            <el-table
              v-loading="assemblyBomFieldLoading || Boolean(activeAssemblyItem && activeAssemblyItem.bomLoading)"
              class="shadcn-data-table"
              data-ui="data-table"
              :data="activeAssemblyBomRows"
              height="100%"
              :empty-text="activeAssemblyItem ? (activeAssemblyItem.bomError ? 'BOM 读取失败' : activeAssemblyItem.bomLoaded ? 'BOM 无原材料明细' : '正在读取 BOM...') : '未选择SKU'">
              <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
              <el-table-column v-for="column in assemblyBomColumns" :key="column.key"
                :prop="column.prop" :label="column.title" :width="column.width"
                :align="column.align" :fixed="column.fixed" show-overflow-tooltip>
                <template slot-scope="scope">
                  <span v-if="column.key === 'SHORTAGEQTY'" :class="scope.row.shortageQty > 0 ? 'danger-text' : 'success-text'">{{ assemblyBomCellValue(scope.row, column) }}</span>
                  <el-tag v-else-if="column.key === 'ALLOCATIONRESULT'" data-ui="badge" size="mini" effect="plain"
                    :type="scope.row.allocationResult === '欠料' ? 'danger' : scope.row.allocationResult === '齐料' ? 'success' : 'info'">{{ assemblyBomCellValue(scope.row, column) }}</el-tag>
                  <span v-else>{{ assemblyBomCellValue(scope.row, column) }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>

        <!-- 上下 Splitter 1 -->
        <div
          class="assembly-row-splitter"
          role="separator"
          title="拖动调整上下区域高度"
          @mousedown="startAssemblyLayerResize(0, $event)"></div>

        <!-- 第 2 层：联合预制作结果 -->
        <section class="assembly-layer" aria-label="联合预制作结果">
          <div class="assembly-layer-head">
            <div class="assembly-layer-title">
              <span>联合预制作结果</span>
              <span class="assembly-layer-meta">已填写 {{ assemblyJointPlanSummary.plannedSkuCount }} 个 SKU</span>
            </div>
            <div class="assembly-layer-actions">
              <span class="assembly-layer-meta">欠料 SKU {{ assemblyJointPlanSummary.shortageSkuCount }} · 缺料明细 {{ assemblyJointPlanSummary.shortageMaterialCount }} 项</span>
              <el-button class="shadcn-button shadcn-button--primary" type="primary" size="mini" icon="el-icon-document-add" :disabled="assemblyJointPlanSummary.shortageMaterialCount === 0" @click="openAssemblyProcurement()">新增采购申请单</el-button>
            </div>
          </div>
          <div class="assembly-layer-table">
            <el-table class="shadcn-data-table" data-ui="data-table" :data="assemblyJointShortageRows" height="100%" :empty-text="assemblyJointResultEmptyText">
              <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
              <el-table-column prop="matNo" label="物料编码" width="108" show-overflow-tooltip></el-table-column>
              <el-table-column prop="matName" label="物料名称" min-width="120" show-overflow-tooltip></el-table-column>
              <el-table-column prop="unit" label="单位" width="54" align="center"></el-table-column>
              <el-table-column prop="demandQty" label="总需求" width="80" align="right"></el-table-column>
              <el-table-column prop="allocatedQty" label="已分配" width="80" align="right"></el-table-column>
              <el-table-column prop="availableQty" label="可用库存" width="80" align="right"></el-table-column>
              <el-table-column prop="shortageQty" label="总欠料" width="80" align="right">
                <template slot-scope="scope"><span class="danger-text">{{ scope.row.shortageQty }}</span></template>
              </el-table-column>
              <el-table-column prop="sourceSkuLabel" label="涉及半成品" min-width="140" show-overflow-tooltip></el-table-column>
              <el-table-column label="操作" width="75" align="center" fixed="right">
                <template slot-scope="scope">
                  <el-button class="shadcn-button shadcn-button--ghost" type="text" size="mini" icon="el-icon-plus" title="针对此项缺料生成采购申请" @click.stop="openAssemblyProcurement(scope.row)">申请</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </section>

        <!-- 上下 Splitter 2 -->
        <div
          class="assembly-row-splitter"
          role="separator"
          title="拖动调整上下区域高度"
          @mousedown="startAssemblyLayerResize(1, $event)"></div>

        <!-- 第 3 层：全部仓库实时库存明细 (V_IC_STKLOG) -->
        <section class="assembly-layer" aria-label="全部仓库实时库存">
          <div class="assembly-layer-head">
            <div class="assembly-layer-title">
              <span>全部仓库实时库存</span>
              <span class="assembly-layer-meta">共 {{ filteredWarehouseMaterials.length }} 项物料</span>
            </div>
            <div class="assembly-layer-actions">
              <el-input
                v-model="warehouseMaterialSearchText"
                class="assembly-warehouse-search"
                size="mini"
                placeholder="搜索物料编码、名称、型号、仓库"
                prefix-icon="el-icon-search"
                clearable></el-input>
            </div>
          </div>
          <div class="assembly-layer-table">
            <el-table
              v-loading="assemblyLiveLoading"
              class="shadcn-data-table"
              data-ui="data-table"
              :data="filteredWarehouseMaterials"
              height="100%"
              row-key="rowKey"
              empty-text="暂无仓库物料">
              <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
              <el-table-column prop="matNo" label="物料编码" width="112" show-overflow-tooltip></el-table-column>
              <el-table-column prop="matName" label="物料名称" min-width="130" show-overflow-tooltip></el-table-column>
              <el-table-column prop="model" label="型号" width="96" show-overflow-tooltip></el-table-column>
              <el-table-column prop="spec" label="规格" min-width="110" show-overflow-tooltip></el-table-column>
              <el-table-column prop="unit" label="单位" width="54" align="center"></el-table-column>
              <el-table-column prop="stockQty" label="物理库存" width="86" align="right"></el-table-column>
              <el-table-column prop="availableQty" label="当前可用量" width="96" align="right">
                <template slot-scope="scope"><strong class="success-text">{{ scope.row.availableQty }}</strong></template>
              </el-table-column>
              <el-table-column prop="warehouse" label="仓库" width="110" show-overflow-tooltip></el-table-column>
            </el-table>
          </div>
        </section>
      </div>
    </div>

    <!-- 弹窗：添加半成品目录 (内置自包含) -->
    <el-dialog title="添加半成品" custom-class="kit-dialog kit-mat-suite-dialog" :visible.sync="assemblyCatalogDialog" width="960px" append-to-body :close-on-click-modal="false">
      <div class="assembly-catalog-filters" role="search" aria-label="半成品查询条件">
        <el-input v-model="assemblyCatalogFilters.documentNo" size="mini" placeholder="单据编码" clearable prefix-icon="el-icon-search"></el-input>
        <el-input v-model="assemblyCatalogFilters.parentProduct" size="mini" placeholder="母件编码" clearable prefix-icon="el-icon-search"></el-input>
        <el-input v-model="assemblyCatalogFilters.name" size="mini" placeholder="产品名称" clearable prefix-icon="el-icon-search"></el-input>
      </div>
      <el-table
        ref="assemblyCatalogTable"
        class="shadcn-data-table"
        :data="assemblyCatalogRows"
        border
        size="mini"
        max-height="400"
        row-key="sku"
        highlight-current-row
        :empty-text="assemblyCatalogEmptyText"
        @row-click="handleAssemblyCatalogRowClick">
        <el-table-column label="选择" width="60" align="center">
          <template slot-scope="{ row }">
            <el-radio
              :value="assemblyCatalogSelectedSku"
              :label="row.sku"
              :disabled="assemblyCatalogConfirmLoading"
              @click.native.stop
              @change="setAssemblyCatalogSelection(row)">
              <span class="assembly-catalog-radio-label">选择 {{ row.documentNo || row.parentProduct || row.sku }}</span>
            </el-radio>
          </template>
        </el-table-column>
        <el-table-column type="index" label="序号" width="50" align="center"></el-table-column>
        <el-table-column prop="documentNo" label="单据编码" width="112" show-overflow-tooltip></el-table-column>
        <el-table-column prop="parentProduct" label="母件编码" width="124" show-overflow-tooltip></el-table-column>
        <el-table-column prop="name" label="产品名称" min-width="140" show-overflow-tooltip></el-table-column>
        <el-table-column prop="spec" label="规格型号" min-width="120" show-overflow-tooltip></el-table-column>
        <el-table-column prop="unit" label="单位" width="54" align="center"></el-table-column>
      </el-table>
      <span slot="footer">
        <el-button class="shadcn-button shadcn-button--outline" size="small" :disabled="assemblyCatalogConfirmLoading" @click="assemblyCatalogDialog = false">取消</el-button>
        <el-button class="shadcn-button shadcn-button--primary" type="primary" size="small" :loading="assemblyCatalogConfirmLoading" :disabled="!assemblyCatalogSelection.length" @click="confirmAssemblyCatalog">
          {{ assemblyCatalogConfirmLoading ? '正在读取 BOM...' : ('确认添加' + (assemblyCatalogSelection.length ? '（' + (assemblyCatalogSelection[0].parentProduct || assemblyCatalogSelection[0].sku) + '）' : '')) }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 弹窗：预测缺料生成采购申请单 (PO_REQ / PO_REQDTL) -->
    <po-req-dialog
      ref="assemblyPoReqDialog"
      @success="handleAssemblyPoReqSuccess">
    </po-req-dialog>
  </div>
</template>

<script>
import { getTabColOrData } from '@/api/user';
import poReqDialog from './po_req_dialog.vue';

const ASSEMBLY_BOM_FIELD_PROPS = Object.freeze({
  MATNO: 'matNo', MATNAME: 'matName', MODEL: 'model', SPEC: 'spec', UNIT: 'unit',
  QTYPER: 'qtyPer', AVAILABLEBEFORE: 'availableBefore', DEMANDQTY: 'demandQty',
  ALLOCATEDQTY: 'allocatedQty', REMAININGAFTER: 'remainingAfter',
  SHORTAGEQTY: 'shortageQty', ALLOCATIONRESULT: 'allocationResult'
});

/**
 * 规范化半成品预制作数输入
 * @param {string|number} value 用户输入的数量
 * @returns {number|string} 合法的大于等于0的整数或空
 */
function normalizeAssemblyPlanQty(value) {
  if (value == null || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) return 0;
  return Math.floor(num);
}

/**
 * 过滤弹窗中的半成品 BOM 母件候选列表
 * @param {Object} row 单条 BOM 母件记录
 * @param {Object} filters 查询条件（单据编码、母件编码、产品名称）
 * @returns {boolean} 是否符合匹配条件
 */
function matchesBomHeaderFilters(row, filters) {
  if (!row || !filters) return true;
  const match = (val, filter) => !filter || String(val || '').toLowerCase().includes(String(filter).toLowerCase());
  return match(row.documentNo, filters.documentNo) &&
    match(row.parentProduct || row.sku, filters.parentProduct) &&
    match(row.name, filters.name);
}

export default {
  name: 'AssemblyWork',
  components: {
    poReqDialog
  },
  data() {
    return {
      // -------------------------------------------------------------
      // 1. ERP 接口与企业身份参数
      // -------------------------------------------------------------
      v_url: 'xuper_auth/getTableRecMany', // 后端通用多记录查询接口
      v_corpid: this.$cookies ? this.$cookies.get('v_corpid') : '', // 企业账套 ID

      // -------------------------------------------------------------
      // 2. 界面布局与拖拽分屏状态
      // -------------------------------------------------------------
      assemblySidebarWidth: 540, // 左侧排产列表宽度 (px)
      assemblyLayerRatios: [0.34, 0.33, 0.33], // 右侧 3 层工作区高度分配比例 (选中物料 / 联合缺料 / 全部库存)
      activeAssemblySku: '', // 当前高亮选中的半成品 SKU
      draggingSku: '', // 正在进行拖拽排序的半成品 SKU

      // -------------------------------------------------------------
      // 3. 左侧半成品排产列表 (工作台核心数据集)
      // -------------------------------------------------------------
      assemblyRows: [], // 用户已添加的半成品列表 [{ sku, name, parentProduct, documentNo, spec, unit, planQty, ... }]

      // -------------------------------------------------------------
      // 4. ERP 实时底表数据与请求控制
      // -------------------------------------------------------------
      assemblyLiveSkuOptions: [], // 从 v_bs_bom 读取的全部半成品母件候选
      assemblyLiveWarehouseMaterials: [], // 从 V_IC_STKLOG 聚合的全部仓库实时库存物料
      assemblyLiveLoading: false, // 实时底表接口加载状态 (控制表格 v-loading)
      assemblyLiveLoaded: false, // 是否已完成初次加载
      assemblyLiveError: '', // 接口异常信息
      assemblyLiveStale: false, // 是否为过期的快照数据
      assemblyLiveRequestId: 0, // 并发请求时序 ID (防止竞态覆盖)
      assemblyLivePromise: null, // 底表加载 Promise 缓存
      assemblyBomFieldRows: [],
      assemblyBomFieldLoading: false,
      assemblyBomFieldError: '',
      assemblyBomPromises: {}, // 各半成品 BOM 子件明细的加载 Promise 缓存
      warehouseMaterialSearchText: '', // 第 3 层全厂实时库存物料搜索关键字

      // -------------------------------------------------------------
      // 5. 弹窗状态管理
      // -------------------------------------------------------------
      // (1) 添加半成品弹窗
      assemblyCatalogDialog: false, // 弹窗显隐控制
      assemblyCatalogConfirmLoading: false, // 确认添加按钮异步加载 BOM 时的 loading 状态
      assemblyCatalogSelection: [], // 弹窗中单选选中的半成品项
      assemblyCatalogFilters: { // 弹窗搜索过滤条件
        documentNo: '', // 单据编码 (IDNO)
        parentProduct: '', // 母件编码 (MATNO)
        name: '' // 产品名称 (MATNM)
      }
    };
  },
  computed: {
    assemblyBomColumns() {
      return this.assemblyBomFieldRows.filter(row => row.THIDE === 'N')
        .slice().sort((a, b) => Number(a.SORTBY || 0) - Number(b.SORTBY || 0))
        .map(row => {
          const key = String(row.key || row.FLDNO || '').toUpperCase();
          return {
            key, prop: ASSEMBLY_BOM_FIELD_PROPS[key],
            title: this.$translate.getColTitle(row, this.$store.state.lang),
            width: Number(row.width || row.WIDTH || row.COLWIDTH) > 0 ? Number(row.width || row.WIDTH || row.COLWIDTH) : 100,
            align: row.align || row.ALIGN || row.CSS || 'left',
            fixed: row.ISFIX === 'Y' ? 'left' : row.ISFIX === 'R' ? 'right' : false
          };
        }).filter(column => column.key && !['SEL', 'ROWNUM', 'OPER'].includes(column.key));
    },

    // -------------------------------------------------------------
    // 计算属性：半成品选项与实时库存
    // -------------------------------------------------------------
    /** 半成品母件选项列表 */
    assemblySkuOptions() {
      return this.assemblyLiveSkuOptions;
    },
    /** 全厂仓库实时库存数据 */
    warehouseMaterials() {
      return this.assemblyLiveWarehouseMaterials;
    },
    /** 第 3 层面板根据关键字过滤后的仓库实时库存列表 (保留真实分仓明细) */
    filteredWarehouseMaterials() {
      const q = (this.warehouseMaterialSearchText || '').trim().toLowerCase();
      if (!q) return this.warehouseMaterials;
      return this.warehouseMaterials.filter(m => (
        String(m.matNo || '').toLowerCase().includes(q) ||
        String(m.matName || '').toLowerCase().includes(q) ||
        String(m.model || '').toLowerCase().includes(q) ||
        String(m.spec || '').toLowerCase().includes(q) ||
        String(m.warehouse || '').toLowerCase().includes(q)
      ));
    },
    /** 右侧 3 层堆叠面板的动态 Grid 布局高度样式 */
    assemblyDetailStyle() {
      return {
        gridTemplateRows: this.assemblyLayerRatios.map(r => `minmax(120px, ${Math.max(15, Math.round(r * 100))}fr)`).join(' 9px ')
      };
    },

    // -------------------------------------------------------------
    // 计算属性：当前选中项与第 1 层 BOM 明细
    // -------------------------------------------------------------
    /** 当前高亮聚焦的排产行数据 */
    activeAssemblyRow() {
      return this.assemblyRows.find(row => row.sku === this.activeAssemblySku) || this.assemblyRows[0] || null;
    },
    /** 当前高亮聚焦的半成品母件元信息 */
    activeAssemblyItem() {
      const row = this.activeAssemblyRow;
      if (!row) return null;
      return this.assemblySkuOptions.find(option => option.sku === row.sku) || row;
    },
    /**
     * 第 1 层：当前选中半成品的原材料明细及在本轮联合排产中的分配结果
     */
    activeAssemblyBomRows() {
      const row = this.activeAssemblyRow;
      const item = this.activeAssemblyItem;
      if (!row || !item || !item.bom || !item.bom.length) return [];
      const planQty = Number(row.planQty) || 0;
      const alloc = this.assemblyJointAllocation[row.sku] || {};
      return item.bom.map((mat, idx) => {
        const rowKey = mat.rowKey || (mat.matNo + '_' + idx);
        const demand = planQty * (mat.qtyPer || 1);
        const matAlloc = (alloc.materials && alloc.materials[rowKey]) || {};
        const allocated = matAlloc.allocatedQty || 0;
        const availableBefore = matAlloc.availableBefore != null ? matAlloc.availableBefore : this.getWarehouseStock(mat.matNo);
        const shortage = Math.max(0, demand - allocated);
        return {
          ...mat,
          demandQty: demand,
          availableBefore,
          allocatedQty: allocated,
          remainingAfter: Math.max(0, availableBefore - allocated),
          shortageQty: shortage,
          allocationResult: planQty === 0 ? '未排产' : (shortage > 0 ? '欠料' : '齐料')
        };
      });
    },

    // -------------------------------------------------------------
    // 计算属性：核心预运算引擎 (联合库存排产与扣减)
    // -------------------------------------------------------------
    /**
     * 联合库存模拟分配核心计算引擎
     * 规则：按左侧排产列表的先后顺序，依次使用全厂可用库存进行扣减模拟分配
     */
    assemblyJointAllocation() {
      // 1. 聚合全厂所有仓库各物料的总可用库存池 (仅正数库存可参与分配)
      const stockPool = {};
      this.warehouseMaterials.forEach(m => {
        if (!m.matNo) return;
        stockPool[m.matNo] = (stockPool[m.matNo] || 0) + (Number(m.availableQty) || 0);
      });
      Object.keys(stockPool).forEach(k => {
        stockPool[k] = Math.max(0, stockPool[k]);
      });

      // 2. 依次遍历每个排产半成品进行模拟分配
      const allocation = {};
      this.assemblyRows.forEach(row => {
        const item = this.assemblySkuOptions.find(opt => opt.sku === row.sku) || row;
        const planQty = Number(row.planQty) || 0;
        const itemMatAlloc = {};
        let itemShortage = false;

        (item.bom || []).forEach((mat, idx) => {
          const rowKey = mat.rowKey || (mat.matNo + '_' + idx);
          const demand = planQty * (mat.qtyPer || 1);
          // 仅大于 0 的库存可参与扣减分配，负库存视为 0 可用
          const currentAvail = Math.max(0, stockPool[mat.matNo] != null ? stockPool[mat.matNo] : 0);
          const allocQty = Math.min(demand, currentAvail);
          // 扣减库存池中的可用量
          stockPool[mat.matNo] = Math.max(0, currentAvail - allocQty);
          if (demand > allocQty) {
            itemShortage = true;
          }
          itemMatAlloc[rowKey] = {
            availableBefore: currentAvail,
            allocatedQty: allocQty,
            shortageQty: Math.max(0, demand - allocQty)
          };
        });

        allocation[row.sku] = {
          planQty,
          isShortage: planQty > 0 && itemShortage,
          isReady: planQty > 0 && !itemShortage,
          materials: itemMatAlloc
        };
      });
      return allocation;
    },

    /**
     * 左侧排产列表中各行的最终排产结果标签 (未填写 / 齐料 / 欠料 / 无BOM)
     */
    assemblyRowResults() {
      const results = {};
      this.assemblyRows.forEach(row => {
        const item = this.assemblySkuOptions.find(opt => opt.sku === row.sku) || row;
        const alloc = this.assemblyJointAllocation[row.sku];
        if (!item || !item.bom || !item.bom.length) {
          results[row.sku] = { label: '无BOM', type: 'info', status: '无BOM' };
        } else if (!alloc || alloc.planQty === 0) {
          results[row.sku] = { label: '未填写', type: 'info', status: '未填写' };
        } else if (alloc.isShortage) {
          results[row.sku] = { label: '欠料', type: 'danger', status: '欠料' };
        } else {
          results[row.sku] = { label: '齐料', type: 'success', status: '齐料' };
        }
      });
      return results;
    },

    /**
     * 第 2 层：汇总所有排产半成品的缺料物料清单及涉及的半成品 (去重显示涉及半成品)
     */
    assemblyJointShortageRows() {
      const shortageMap = {};
      this.assemblyRows.forEach(row => {
        const item = this.assemblySkuOptions.find(opt => opt.sku === row.sku) || row;
        const alloc = this.assemblyJointAllocation[row.sku];
        if (!alloc || alloc.planQty === 0) return;
        (item.bom || []).forEach((mat, idx) => {
          const rowKey = mat.rowKey || (mat.matNo + '_' + idx);
          const matAlloc = alloc.materials[rowKey];
          if (matAlloc && matAlloc.shortageQty > 0) {
            if (!shortageMap[mat.matNo]) {
              shortageMap[mat.matNo] = {
                matNo: mat.matNo,
                matName: mat.matName,
                unit: mat.unit,
                demandQty: 0,
                allocatedQty: 0,
                availableQty: Math.max(0, this.getWarehouseStock(mat.matNo)),
                shortageQty: 0,
                sourceSkus: []
              };
            }
            shortageMap[mat.matNo].demandQty += alloc.planQty * (mat.qtyPer || 1);
            shortageMap[mat.matNo].allocatedQty += matAlloc.allocatedQty;
            shortageMap[mat.matNo].shortageQty += matAlloc.shortageQty;
            if (!shortageMap[mat.matNo].sourceSkus.includes(row.sku)) {
              shortageMap[mat.matNo].sourceSkus.push(row.sku);
            }
          }
        });
      });
      return Object.values(shortageMap).map(r => ({
        ...r,
        sourceSkuLabel: Array.from(new Set(r.sourceSkus)).join(', '),
        result: '欠料'
      }));
    },

    /**
     * 联合预制作结果汇总统计 (已填写半成品数 / 欠料半成品数 / 缺料物料项数)
     */
    assemblyJointPlanSummary() {
      const planned = this.assemblyRows.filter(r => Number(r.planQty) > 0);
      const shortage = planned.filter(r => this.assemblyRowResults[r.sku] && this.assemblyRowResults[r.sku].status === '欠料');
      return {
        plannedSkuCount: planned.length,
        shortageSkuCount: shortage.length,
        shortageMaterialCount: this.assemblyJointShortageRows.length
      };
    },

    /** 第 2 层表格无数据时的友好空提示 */
    assemblyJointResultEmptyText() {
      if (!this.assemblyRows.some(r => Number(r.planQty) > 0)) return '请在左侧半成品列表输入预制作数';
      return this.assemblyJointShortageRows.length === 0 ? '当前排产无缺料物料' : '暂无数据';
    },

    /** Radio 与确认按钮共用同一份选中状态 */
    assemblyCatalogSelectedSku() {
      return this.assemblyCatalogSelection.length ? this.assemblyCatalogSelection[0].sku : null;
    },

    /** 弹窗中经条件过滤后的半成品候选记录 */
    assemblyCatalogRows() {
      return this.assemblySkuOptions.filter(row => matchesBomHeaderFilters(row, this.assemblyCatalogFilters));
    },

    /** 弹窗表格空提示 */
    assemblyCatalogEmptyText() {
      if (this.assemblyLiveLoading) return '正在加载半成品目录...';
      return '暂无半成品目录数据';
    }
  },

  created() {
    this._assemblyBomFieldsDisposed = false;
    this.loadAssemblyBomFields();
    // 初始化账套 ID 并加载半成品母件底表与实时库存
    this.v_corpid = this.$cookies ? this.$cookies.get('v_corpid') : '';
    this.loadAssemblyLiveData();
  },

  beforeDestroy() {
    this._assemblyBomFieldsDisposed = true;
  },

  methods: {
    assemblyBomCellValue(row, column) {
      const value = column.prop ? row[column.prop] : null;
      return value == null || value === '' ? '—' : value;
    },
    async loadAssemblyBomFields() {
      if (this._assemblyBomFieldsDisposed || this.assemblyBomFieldLoading) return;
      this.assemblyBomFieldLoading = true;
      this.assemblyBomFieldError = '';
      this.assemblyBomFieldRows = [];
      try {
        const res = await getTabColOrData('xuper_auth/getBaseData', 'v_sys_form_fld',
          'idseq', '490050', 'lstseq', '4', '', '', ' order by sortby,lstseq');
        if (this._assemblyBomFieldsDisposed) return;
        if (!res || String(res.code) !== '200' || !Array.isArray(res.result)) {
          throw new Error((res && (typeof res.result === 'string' ? res.result : res.message)) || '制作组成字段读取失败');
        }
        this.assemblyBomFieldRows = res.result;
      } catch (error) {
        if (!this._assemblyBomFieldsDisposed) this.assemblyBomFieldError = error.message || '制作组成字段读取失败';
      } finally {
        if (!this._assemblyBomFieldsDisposed) this.assemblyBomFieldLoading = false;
      }
    },
    // -------------------------------------------------------------
    // 1. 数据加载与 ERP 接口对接
    // -------------------------------------------------------------
    /**
     * 加载半成品 BOM 母件台账 (v_bs_bom) 与全厂仓库实时库存 (V_IC_STKLOG)
     * @param {boolean} force 是否强制重新请求
     */
    loadAssemblyLiveData(force) {
      if (this.assemblyLiveLoading) return this.assemblyLivePromise;
      if (this.assemblyLiveLoaded && !force) return Promise.resolve();
      const requestId = this.assemblyLiveRequestId + 1;
      this.assemblyLiveRequestId = requestId;
      this.assemblyLiveLoading = true;
      this.assemblyLiveError = '';
      this.assemblyLiveStale = Boolean(force && this.assemblyLiveLoaded);
      const corpid = this.v_corpid;

      const promise = Promise.all([
        // 查询半成品 BOM 母件 (v_bs_bom)
        getTabColOrData(
          this.v_url,
          'v_bs_bom',
          '',
          '',
          'CORPID',
          corpid,
          '',
          '',
          " and MATNO like '%%' and ishide='N' order by MATNO desc"
        ),
        // 查询仓库实时库存 (V_IC_STKLOG)
        getTabColOrData(
          this.v_url,
          'V_IC_STKLOG',
          'corpid',
          corpid,
          '',
          '',
          '',
          '',
          ' and qty>0 order by STKNM desc'
        )
      ]).then(([bomRes, stockRes]) => {
        if (requestId !== this.assemblyLiveRequestId) return;
        const rawBoms = (bomRes && bomRes.code === '200' && Array.isArray(bomRes.result)) ? bomRes.result : [];
        const rawStocks = (stockRes && stockRes.code === '200' && Array.isArray(stockRes.result)) ? stockRes.result : [];

        // 格式化半成品母件台账
        this.assemblyLiveSkuOptions = rawBoms.map(row => ({
          sku: row.MATNO || row.IDNO || '',
          name: row.MATNM || '',
          parentProduct: row.MATNO || '',
          documentNo: row.IDNO || '',
          spec: row.SPECNM || '',
          unit: row.UNITNM || '',
          sourceId: row.IDSEQ || '',
          bom: [],
          bomLoaded: false,
          bomLoading: false,
          bomError: ''
        }));

        // 保留全厂各个仓库的完整物理库存与可用量明细 (不进行单一物料合并压扁，防止分仓信息丢失)
        this.assemblyLiveWarehouseMaterials = rawStocks.map((item, idx) => ({
          rowKey: (item.MATNO || '') + '_' + (item.STKNM || item.STKNO || '') + '_' + idx,
          matNo: item.MATNO || '',
          matName: item.MATNM || '',
          model: item.MODELNM || '',
          spec: item.SPECNM || item.MATDESC || '',
          unit: item.UNITNM || '',
          stockQty: Number(item.QTY) || 0,
          availableQty: Number(item.OKQTY != null ? item.OKQTY : item.QTY) || 0,
          warehouse: item.STKNM || item.STKNO || '',
          warehouseNo: item.STKNO || ''
        }));

        this.assemblyLiveLoaded = true;
        this.assemblyLiveStale = false;
      }).catch(error => {
        if (requestId !== this.assemblyLiveRequestId) return;
        this.assemblyLiveError = error && error.message ? error.message : 'ERP 数据读取失败';
        this.$message.error(this.assemblyLiveError);
      }).finally(() => {
        if (requestId === this.assemblyLiveRequestId) {
          this.assemblyLiveLoading = false;
          this.assemblyLivePromise = null;
        }
      });
      this.assemblyLivePromise = promise;
      return promise;
    },

    /**
     * 动态加载选定半成品的 BOM 子件定额明细 (v_bs_bomdtl)
     * @param {Object} item 目标半成品对象
     */
    loadAssemblyBom(item) {
      if (!item || item.bomLoaded || item.bomLoading) return this.assemblyBomPromises[item && item.sku];
      if (!item.sourceId) {
        this.$set(item, 'bomLoaded', true);
        this.$set(item, 'bomError', '缺少 BOM IDSEQ');
        return Promise.resolve();
      }
      this.$set(item, 'bomLoading', true);
      this.$set(item, 'bomError', '');

      const promise = getTabColOrData(
        this.v_url,
        'v_bs_bomdtl',
        'idseq',
        item.sourceId,
        '',
        '',
        '',
        '',
        ' order by sortby, matno'
      ).then(res => {
        const rows = (res && res.code === '200' && Array.isArray(res.result)) ? res.result : [];
        const bomItems = rows.map((dtl, idx) => ({
          rowKey: (dtl.MATNO || '') + '_' + idx,
          matNo: dtl.MATNO || '',
          matName: dtl.MATNM || dtl.CMATNM || '',
          model: dtl.MODELNM || '',
          spec: dtl.SPECNM || '',
          unit: dtl.UNITNM || '',
          qtyPer: Number(dtl.QTY || dtl.USEQTY || 1)
        }));
        this.$set(item, 'bom', bomItems);
        this.$set(item, 'bomLoaded', true);
        return bomItems;
      }).catch(error => {
        this.$set(item, 'bomError', error && error.message ? error.message : 'BOM 读取失败');
        return [];
      }).finally(() => {
        this.$set(item, 'bomLoading', false);
        delete this.assemblyBomPromises[item.sku];
      });

      this.$set(this.assemblyBomPromises, item.sku, promise);
      return promise;
    },

    // -------------------------------------------------------------
    // 2. 辅助工具与行交互方法
    // -------------------------------------------------------------
    /** 获取某物料在全厂所有仓库的当前可用库存总量 (累加各分仓 availableQty) */
    getWarehouseStock(matNo) {
      if (!matNo) return 0;
      return this.warehouseMaterials
        .filter(x => x.matNo === matNo)
        .reduce((sum, item) => sum + (Number(item.availableQty) || 0), 0);
    },
    /** 根据排产行匹配母件元数据 */
    assemblySkuOptionForRow(row) {
      return row ? this.assemblySkuOptions.find(opt => opt.sku === row.sku) || row : null;
    },
    /** 表格列 formatter：格式化独占可制作数 */
    assemblyRowExclusiveBuildableQty(row) {
      const item = row && row.bom && row.bom.length ? row : this.assemblySkuOptionForRow(row);
      return this.assemblyExclusiveBuildableQty(item);
    },
    /** 计算单个半成品的独占可制作数 min(可用量 / 单台用量) (合并相同物料单台定额，负库存按0处理) */
    assemblyExclusiveBuildableQty(item) {
      if (!item || !item.bom || !item.bom.length) return 0;
      // 1. 汇总该 BOM 内部各物料的总单台用量 (合并相同 matNo 的定额)
      const totalMatQtyMap = {};
      item.bom.forEach(part => {
        if (!part.matNo) return;
        totalMatQtyMap[part.matNo] = (totalMatQtyMap[part.matNo] || 0) + (Number(part.qtyPer) || 0);
      });

      const limits = Object.entries(totalMatQtyMap).map(([matNo, totalQtyPer]) => {
        if (totalQtyPer <= 0) return 0;
        // 负库存可用量视为 0，可制作数必须 >= 0
        const stock = Math.max(0, this.getWarehouseStock(matNo));
        return Math.floor(stock / totalQtyPer);
      });
      return limits.length ? Math.max(0, Math.min(...limits)) : 0;
    },
    /** 处理用户输入预制作数 */
    handleAssemblyPlanQtyInput(row, value) {
      this.$set(row, 'planQty', normalizeAssemblyPlanQty(value));
    },
    /** 点击排产行：设置当前高亮项，并自动按需加载其 BOM 明细 */
    selectAssemblyRow(row) {
      this.activeAssemblySku = row.sku;
      const item = this.assemblySkuOptionForRow(row);
      if (item && !item.bomLoaded) {
        this.loadAssemblyBom(item);
      }
    },
    /** 动态设置排产列表行类名 (高亮激活与拖拽目标) */
    assemblyRowClassName({ row }) {
      const classes = ['assembly-draggable-row'];
      if (row.sku === this.activeAssemblySku) classes.push('is-drop-target');
      return classes.join(' ');
    },
    /** 根据排产状态返回 Tag 徽章类型 */
    assemblyResultTagType(result) {
      return !result || ['未填写', '加载中', '无BOM'].indexOf(result.status) >= 0 ? 'info' : result.status === '齐料' ? 'success' : 'danger';
    },
    /** 从排产列表中移除某半成品 */
    removeAssemblyRow(row) {
      this.assemblyRows = this.assemblyRows.filter(r => r.sku !== row.sku);
      if (this.activeAssemblySku === row.sku && this.assemblyRows.length) {
        this.activeAssemblySku = this.assemblyRows[0].sku;
      }
    },

    // -------------------------------------------------------------
    // 3. 表格行拖拽排序 (Drag & Drop)
    // -------------------------------------------------------------
    /** 开始拖拽排产行 */
    startAssemblyRowDrag(e) {
      const tr = e.target.closest('tr');
      if (!tr) return;
      this.draggingSku = tr.__vue__ ? tr.__vue__.row && tr.__vue__.row.sku : '';
      e.dataTransfer.effectAllowed = 'move';
    },
    /** 拖拽悬停 */
    overAssemblyRowDrag(e) {
      e.preventDefault();
    },
    /** 放置行：完成在排产列表中的位置置换 */
    dropAssemblyRowDrag(e) {
      const tr = e.target.closest('tr');
      if (!tr) return;
      const targetSku = tr.__vue__ && tr.__vue__.row && tr.__vue__.row.sku;
      if (!this.draggingSku || !targetSku || this.draggingSku === targetSku) return;
      const from = this.assemblyRows.findIndex(r => r.sku === this.draggingSku);
      const to = this.assemblyRows.findIndex(r => r.sku === targetSku);
      if (from >= 0 && to >= 0) {
        const item = this.assemblyRows.splice(from, 1)[0];
        this.assemblyRows.splice(to, 0, item);
      }
      this.draggingSku = '';
    },
    /** 结束拖拽 */
    endAssemblyRowDrag() {
      this.draggingSku = '';
    },

    // -------------------------------------------------------------
    // 4. 分屏拖拽与高度调整 Splitter
    // -------------------------------------------------------------
    /** 左右拖拽条：调整左侧排产列表宽度 */
    startAssemblyColumnResize(event) {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = this.assemblySidebarWidth;
      const move = ev => {
        this.assemblySidebarWidth = Math.max(300, Math.min(800, startWidth + ev.clientX - startX));
      };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    },
    /** 上下拖拽条：调整右侧 3 层工作区高度分配比例 */
    startAssemblyLayerResize(index, event) {
      event.preventDefault();
      const startY = event.clientY;
      const startRatios = this.assemblyLayerRatios.slice();
      const stack = this.$refs.assemblyDetailStack;
      const usableH = stack ? stack.clientHeight : 600;
      const move = ev => {
        const delta = (ev.clientY - startY) / usableH;
        const next = startRatios.slice();
        next[index] = Math.max(0.15, startRatios[index] + delta);
        next[index + 1] = Math.max(0.15, startRatios[index + 1] - delta);
        const sum = next.reduce((a, b) => a + b, 0);
        this.assemblyLayerRatios = next.map(x => x / sum);
      };
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    },

    // -------------------------------------------------------------
    // 5. 添加半成品弹窗交互 (单选控制与状态回显)
    // -------------------------------------------------------------
    /** 同步单选数据与高亮；传 null 表示取消选择 */
    setAssemblyCatalogSelection(row) {
      this.assemblyCatalogSelection = row ? [row] : [];
      // 等待数据更新和表格内部行点击处理完成后，再同步高亮。
      this.$nextTick(() => {
        const table = this.$refs.assemblyCatalogTable;
        if (table) table.setCurrentRow(this.assemblyCatalogSelection[0] || null);
      });
    },
    /** 打开弹窗：清除旧筛选，回显当前激活的 SKU */
    openAssemblyCatalog() {
      this.assemblyCatalogFilters.documentNo = '';
      this.assemblyCatalogFilters.parentProduct = '';
      this.assemblyCatalogFilters.name = '';
      this.assemblyCatalogDialog = true;
      const targetSku = this.activeAssemblySku || (this.assemblyRows.length ? this.assemblyRows[0].sku : '');
      const targetRow = targetSku
        ? this.assemblyCatalogRows.find(row => row.sku === targetSku)
        : null;
      this.setAssemblyCatalogSelection(targetRow || null);
    },
    /** 点击整行单选；再次点击已选行取消选择 */
    handleAssemblyCatalogRowClick(row) {
      if (this.assemblyCatalogConfirmLoading) {
        this.setAssemblyCatalogSelection(this.assemblyCatalogSelection[0] || null);
        return;
      }
      const isSelected = this.assemblyCatalogSelectedSku === row.sku;
      this.setAssemblyCatalogSelection(isSelected ? null : row);
    },
    /** 确认添加：按钮 loading，在弹窗内先完成 BOM 查询，成功后还原按钮、关闭弹窗、渲染数据 */
    async confirmAssemblyCatalog() {
      if (!this.assemblyCatalogSelection.length || this.assemblyCatalogConfirmLoading) return;
      const selected = this.assemblyCatalogSelection[0];
      this.assemblyCatalogConfirmLoading = true;

      let targetItem = this.assemblyRows.find(r => r.sku === selected.sku);
      if (!targetItem) {
        targetItem = {
          ...selected,
          planQty: '' // 默认预制作数为空，不自动填充
        };
        this.assemblyRows.push(targetItem);
      }
      this.activeAssemblySku = targetItem.sku;

      try {
        // 先在弹窗内异步加载 BOM 物料
        await this.loadAssemblyBom(targetItem);
        this.assemblyCatalogConfirmLoading = false;
        this.assemblyCatalogDialog = false;
      } catch (err) {
        this.assemblyCatalogConfirmLoading = false;
        this.$message.error('半成品 BOM 读取失败：' + (err && err.message ? err.message : '请重试'));
      }
    },

    // -------------------------------------------------------------
    // 6. 下推生成采购申请单 (PO_REQ / PO_REQDTL)
    // -------------------------------------------------------------
    /** 打开新增采购申请单弹窗 (支持针对全部预测缺料或单行缺料) */
    openAssemblyProcurement(targetRow) {
      const rows = targetRow ? [targetRow] : this.assemblyJointShortageRows;
      if (!rows || !rows.length) {
        this.$message.warning('当前暂无预测欠料物料明细');
        return;
      }
      const plannedSkus = this.assemblyRows.filter(r => Number(r.planQty) > 0).map(r => r.sku);
      this.$refs.assemblyPoReqDialog.openWithShortageMaterials(rows, {
        title: '部装预制作采购申请单',
        deptNo: '生产部',
        remark: plannedSkus.length ? `部装半成品预制作欠料申请 (涉及部件: ${plannedSkus.join('、')})` : '部装半成品预制作欠料申请'
      });
    },
    /** 采购申请单保存成功后回调：刷新实时库存底表 */
    handleAssemblyPoReqSuccess(res) {
      setTimeout(() => {
        this.fetchLiveWarehouseMaterials();
      }, 1000);
    }
  }
};
</script>

<style scoped>
.assembly-bom-field-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
  padding: 4px 12px;
  color: #dc2626;
}

.assembly-catalog-radio-label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.kit-material-workbench {
  --background: #f6f7f9;
  --foreground: #0b1019;
  --card: #ffffff;
  --card-foreground: #0b1019;
  --popover: #ffffff;
  --popover-foreground: #0b1019;
  --primary: #2563eb;
  --primary-foreground: #ffffff;
  --secondary: #f4f4f5;
  --secondary-foreground: #0b1019;
  --muted: #f4f4f5;
  --muted-foreground: #a6abb4;
  --accent: #eff6ff;
  --accent-foreground: #1d4ed8;
  --destructive: #dc2626;
  --destructive-foreground: #ffffff;
  --border: #e4e4e7;
  --input: #d4d4d8;
  --ring: #2563eb;
  --success: #15803d;
  --success-soft: #ecfdf3;
  --warning: #b45309;
  --warning-soft: #fff7ed;
  --shortage-row: #fff1f2;
  --shortage-row-hover: #ffe4e6;
  --radius: 6px;
  --font-size-body: 12px;
  --font-size-table-header: 12px;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--background);
  color: var(--foreground);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: var(--font-size-body);
  box-sizing: border-box;
}

.assembly-workbench {
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--background);
}

.assembly-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 320px;
  max-width: 820px;
  border-right: 1px solid var(--border);
  background: var(--card);
  overflow: hidden;
}

.assembly-sidebar-table {
  flex: 1 1 auto;
  min-height: 0;
  height: calc(100% - 46px);
  overflow: hidden;
}

.assembly-sidebar-footer {
  flex: 0 0 46px;
  height: 46px;
  min-height: 46px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 12px;
  border-top: 1px solid var(--border);
  background: #fafafa;
}

.assembly-column-splitter {
  flex: 0 0 9px;
  cursor: col-resize;
  background: var(--muted);
  border-left: 1px solid var(--border);
  border-right: 1px solid var(--border);
  position: relative;
}
.assembly-column-splitter::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 3px;
  width: 3px;
  height: 42px;
  border-radius: 3px;
  background: var(--input);
  transform: translateY(-50%);
}
.assembly-column-splitter:hover::after {
  background: var(--primary);
}

.assembly-detail-stack {
  display: grid;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: var(--background);
}

.assembly-layer {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--card);
  border: 1px solid var(--border);
  overflow: hidden;
}

.assembly-layer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 36px;
  padding: 0 12px;
  border-bottom: 1px solid var(--border);
  background: #fafafa;
}
.assembly-layer-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 12px;
}
.assembly-layer-meta {
  color: var(--muted-foreground);
  font-size: 12px;
  font-weight: 400;
}
.assembly-layer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.assembly-layer-table {
  flex: 1 1 auto;
  min-height: 0;
}

.assembly-row-splitter {
  height: 9px;
  cursor: row-resize;
  background: var(--muted);
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  position: relative;
}
.assembly-row-splitter::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 50%;
  width: 42px;
  height: 3px;
  border-radius: 3px;
  background: var(--input);
  transform: translateX(-50%);
}
.assembly-row-splitter:hover::after {
  background: var(--primary);
}

.assembly-sku-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.assembly-order-index {
  color: var(--muted-foreground);
  font-size: 11px;
}
.assembly-sku-text {
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
}
.assembly-sku-text:hover {
  text-decoration: underline;
}

.assembly-plan-qty-input ::v-deep .el-input__inner {
  height: 22px;
  line-height: 22px;
  padding: 0 4px;
  text-align: center;
}

.assembly-warehouse-search {
  width: 220px;
}

.assembly-catalog-search-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}

.assembly-procurement-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.dialog-meta {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid var(--border);
  border-radius: 5px;
}
.dialog-meta span { color: var(--muted-foreground); }
.dialog-meta strong { margin-left: 5px; color: var(--foreground); font-weight: 600; }

.danger-text { color: var(--destructive); font-weight: 600; }
.success-text { color: var(--success); font-weight: 600; }
.warning-text { color: var(--warning); font-weight: 600; }

::v-deep .el-table tbody tr { height: 32px; }
::v-deep .el-table tbody td { height: 31px; padding: 0 !important; }
::v-deep .el-table tbody .cell {
  color: var(--foreground);
  font-size: var(--font-size-body);
  height: 31px;
  line-height: 31px;
  padding-left: 8px;
  padding-right: 8px;
  box-sizing: border-box;
}

::v-deep .el-table thead th {
  height: 32px;
  padding: 0 !important;
  vertical-align: middle;
  background: #fff !important;
  font-weight: var(--font-weight-table-header);
  font-size: var(--font-size-table-header);
  color: var(--foreground);
}
::v-deep .el-table thead th .cell {
  height: 31px;
  line-height: 31px;
  padding-left: 8px;
  padding-right: 8px;
  white-space: nowrap;
}

::v-deep .shadcn-button {
  height: 30px;
  padding: 0 10px;
  border-radius: var(--radius);
  font-size: 12px;
  font-weight: 550;
}
::v-deep .shadcn-button--primary { color: var(--primary-foreground); background: var(--primary); border-color: var(--primary); }
::v-deep .shadcn-button--outline { color: var(--foreground); background: var(--card); border-color: var(--input); }
::v-deep .shadcn-button--ghost { color: var(--muted-foreground); background: transparent; border-color: transparent; }
::v-deep .shadcn-button--ghost:hover { color: var(--foreground); background: var(--muted); }
</style>
